"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { FileDown, Check } from "lucide-react";

const EMPLOYEES = [
  { name: "Maria L.", hours: 40, ot: 0, rate: 18, total: 720 },
  { name: "James C.", hours: 32, ot: 0, rate: 16, total: 512 },
  { name: "Carlos R.", hours: 28, ot: 0, rate: 15, total: 420 },
  { name: "Sofia M.", hours: 40, ot: 4, rate: 17, total: 782 },
  { name: "Alex P.", hours: 28, ot: 0, rate: 16, total: 448 },
];

export default function TimeClockPayroll() {
  const { t } = useLang();
  const totalHours = EMPLOYEES.reduce((s, e) => s + e.hours + e.ot, 0);
  const totalCost = EMPLOYEES.reduce((s, e) => s + e.total, 0);

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Friday morning payroll", "Nómina de viernes")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Minutes. ", "Minutos. ")}
            <span className="text-redy-charcoal/30">{t("Not hours.", "No horas.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Timesheets sum themselves. Overtime calculates. Hours, overtime and tips export to a spreadsheet you hand to payroll. Done before the staff arrives.",
              "Los timesheets se suman solos. El overtime calcula. Exportas horas, overtime y propinas en una planilla para tu nómina. Listo antes de que llegue el staff."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[24px] p-6 md:p-7 max-w-[820px] mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
        >
          {/* Header */}
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                {t("Payroll · Week ending Sep 14", "Nómina · Semana 14 Sep")}
              </div>
              <div className="text-[16px] font-black text-redy-charcoal">
                {EMPLOYEES.length} {t("employees", "empleados")}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/55">
                {t("Total", "Total")}
              </div>
              <div
                className="text-[20px] font-black tabular-nums"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                ${totalCost.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Employee rows */}
          <div className="space-y-2 mb-6">
            {EMPLOYEES.map((e, i) => (
              <motion.div
                key={e.name}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: 0.15 + 0.08 * i }}
                className="bg-[#FAFAF8] rounded-[14px] p-3.5 grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center"
              >
                <div>
                  <div className="text-[13px] font-black text-redy-charcoal">{e.name}</div>
                  <div className="text-[10px] text-redy-secondary">${e.rate}/{t("hr", "hr")}</div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] font-bold text-redy-charcoal tabular-nums">{e.hours}h</div>
                  {e.ot > 0 && (
                    <div className="text-[9px] font-black uppercase tracking-wider" style={{ color: "#F4722B" }}>
                      +{e.ot}h OT
                    </div>
                  )}
                </div>
                <Check className="w-4 h-4" style={{ color: "#2ECC71" }} strokeWidth={3} />
                <div className="text-[15px] font-black tabular-nums text-redy-charcoal w-16 text-right">
                  ${e.total}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Totals + export */}
          <div className="pt-5 border-t border-black/5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-center">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                  {t("Total hours", "Horas totales")}
                </div>
                <div className="text-[14px] font-black text-redy-charcoal tabular-nums">{totalHours}</div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                  {t("OT hours", "Horas OT")}
                </div>
                <div className="text-[14px] font-black tabular-nums" style={{ color: "#F4722B" }}>4</div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/60">
                  {t("Approved", "Aprobado")}
                </div>
                <div className="text-[14px] font-black text-redy-charcoal tabular-nums">5/5</div>
              </div>
            </div>

            <div
              className="inline-flex items-center gap-2 text-[12px] font-bold text-white px-4 py-2.5 rounded-full"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              }}
            >
              <FileDown className="w-3.5 h-3.5" />
              {t("Export · Hours · OT · Tips", "Exportar · Horas · OT · Propinas")}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
